import { createLeafletDriverIcon } from './DriverMarkerIcon';
import { DriverPopupContent } from './DriverPopupContent';
import { DriverMapLabel } from './DriverMapLabel';
import { useEffect, useRef } from 'react';
import { useActiveDriver } from '../hooks';
import { ActiveDriver } from '../models';
import { animateMarker } from '../utils';
import L from 'leaflet';

interface DriverMapProps {
  driver: ActiveDriver;
  className?: string;
}

const DriverMap = ({
  driver: initialDriver,
  className = '',
}: DriverMapProps) => {
  const { driver: updatedDriver } = useActiveDriver(initialDriver.driverId);
  const driver = updatedDriver || initialDriver;
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);

  useEffect(() => {
    if (mapRef.current && !mapInstance.current) {
      try {
        mapInstance.current = L.map(mapRef.current, {
          center: [driver.latitude, driver.longitude],
          zoom: 15,
          dragging: false,
          zoomControl: false,
          scrollWheelZoom: false,
          doubleClickZoom: false,
          boxZoom: false,
          keyboard: false,
          touchZoom: false,
        });

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '© OpenStreetMap contributors',
        }).addTo(mapInstance.current);

        const driverIcon = createLeafletDriverIcon(driver.course || 0);

        markerRef.current = L.marker([driver.latitude, driver.longitude], {
          icon: driverIcon,
        }).addTo(mapInstance.current);

        const popupContent = DriverPopupContent({ driver });
        markerRef.current.bindPopup(popupContent, {
          className: 'driver-popup',
        });

        mapInstance.current.whenReady(() => {
          setTimeout(() => mapInstance.current?.invalidateSize(), 0);
        });

        const resizeObserver = new ResizeObserver(() => {
          mapInstance.current?.invalidateSize();
        });
        resizeObserver.observe(mapRef.current);

        return () => {
          resizeObserver.disconnect();
          if (mapInstance.current) {
            mapInstance.current.remove();
            mapInstance.current = null;
            markerRef.current = null;
          }
        };
      } catch (error) {
        console.error('Error inicializando mapa:', error);
      }
    }
  }, []);

  useEffect(() => {
    if (!markerRef.current || !mapInstance.current) return;

    const newPosition: L.LatLngExpression = [driver.latitude, driver.longitude];
    const currentPosition = markerRef.current.getLatLng();

    const positionChanged =
      currentPosition.lat !== driver.latitude ||
      currentPosition.lng !== driver.longitude;

    if (positionChanged) {
      animateMarker(markerRef.current, newPosition, {
        duration: 2000,
      });

      mapInstance.current.panTo(newPosition, {
        animate: true,
        duration: 2,
      });
    }

    const updatedIcon = createLeafletDriverIcon(driver.course || 0);
    markerRef.current.setIcon(updatedIcon);

    const popupContent = DriverPopupContent({ driver });
    markerRef.current.setPopupContent(popupContent);
  }, [
    driver.latitude,
    driver.longitude,
    driver.driverId,
    driver.firstName,
    driver.lastName,
    driver.status,
    driver.speed,
    driver.course,
    driver.batteryLevel,
    driver.updatedAt,
  ]);

  return (
    <div className={`relative isolate w-full h-full ${className}`}>
      <DriverMapLabel driver={driver} />
      <div
        ref={mapRef}
        className='w-full h-full'
        style={{
          cursor: 'default',
        }}
      />
    </div>
  );
};

export default DriverMap;
